<script setup>
import Swal from "sweetalert2"
import { computed } from "vue"
import {
  MAX_PASAJEROS,
  MAX_INFANTES_POR_ADULTO,
  restriccionGoogleFlightsDe
} from "@/composables/useHabitaciones"

const props = defineProps({
  habitaciones: {
    type: Array,
    required: true
  },

  colClass: {
    type: String,
    default: "col-md-12"
  },

  totalPersonas: {
    type: Number,
    required: true
  },

  agregarHabitacion: {
    type: Function,
    required: true
  },

  eliminarHabitacion: {
    type: Function,
    required: true
  }
})

const emit = defineEmits([
  "update:adultos",
  "update:ninos",
  "update:infantes"
])

// Combinación actual que Google Flights no admitiría (se muestra en vivo)
const restriccionActual = computed(() =>
  restriccionGoogleFlightsDe(props.habitaciones)
)

// =========================
// GRID EDADES
// =========================

const colEdadClass = (cantidad) => {

  switch (cantidad) {

    case 1:
      return "col-md-12"

    case 2:
      return "col-md-6"

    case 3:
      return "col-md-4"

    case 4:
      return "col-md-3"

    default:
      return "col-md-3"
  }

}

// =========================
// VALIDAR LIMITE
// =========================

const puedeAgregar = (valorActual, nuevoValor) => {

  const totalActual = Number(props.totalPersonas)

  const diferencia =
    Number(nuevoValor) - Number(valorActual)

  return totalActual + diferencia <= 9

}

// =========================
// ADULTOS
// =========================

function setAdultos(index, value, event) {

  const hab = props.habitaciones[index]

  if (!puedeAgregar(hab.adultos, value)) {

    Swal.fire({
      icon: "error",
      title: "Límite excedido",
      text: "Máximo 9 personas en total"
    })

    event.target.value = hab.adultos

    return
  }

  emit("update:adultos", {
    index,
    value
  })

}

// =========================
// NIÑOS
// =========================

function setNinos(index, value, event) {

  const hab = props.habitaciones[index]

  if (!puedeAgregar(hab.ninos, value)) {

    Swal.fire({
      icon: "error",
      title: "Límite excedido",
      text: "Máximo 9 personas en total"
    })

    event.target.value = hab.ninos

    return
  }

  emit("update:ninos", {
    index,
    value
  })

}

// =========================
// INFANTES
// =========================

function setInfantes(index, value, event) {

  const hab = props.habitaciones[index]

  if (!puedeAgregar(hab.infantes, value)) {

    Swal.fire({
      icon: "error",
      title: "Límite excedido",
      text: "Máximo 9 personas en total"
    })

    event.target.value = hab.infantes

    return
  }

  emit("update:infantes", {
    index,
    value
  })

}

// Niño = de 2 a 17 años (menores van como infantes). Se corrige en @change
// (al salir del campo) y no en @input: si no, escribir "12" se rompería al
// teclear el "1".
const EDAD_MIN_NINO = 2
const EDAD_MAX_NINO = 17

function setEdadNino(hab, j, event) {

  const raw = event.target.value

  const edad = raw === ""
    ? hab.edadesNinos[j]
    : Math.min(Math.max(Math.trunc(Number(raw)) || 0, EDAD_MIN_NINO), EDAD_MAX_NINO)

  hab.edadesNinos[j] = edad
  event.target.value = edad

}

// Infante = hasta 1 año. El atributo max solo limita las flechas del input,
// así que también se corrige lo que se escriba a mano (fuera de 0–1).
const EDAD_MAX_INFANTE = 1

function setEdadInfante(hab, j, event) {

  const raw = event.target.value

  // Permite borrar el campo mientras se escribe
  if (raw === "") return

  const edad = Math.min(Math.max(Math.trunc(Number(raw)) || 0, 0), EDAD_MAX_INFANTE)

  hab.edadesInfantes[j] = edad
  event.target.value = edad

}
</script>

<template>

  <div class="col-12 mt-3">

    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-center mb-3">

      <label class="form-label fw-bold mb-0">

        <i class="bi bi-door-open"></i>
        Habitaciones

      </label>

      <div class="d-flex gap-2">

        <!-- AGREGAR -->
        <button
          type="button"
          class="btn btn-success btn-sm"
          @click="agregarHabitacion"
        >

          <i class="bi bi-plus-lg"></i>

        </button>

        <!-- ELIMINAR -->
        <button
          type="button"
          class="btn btn-danger btn-sm"
          @click="eliminarHabitacion"
        >

          <i class="bi bi-dash-lg"></i>

        </button>

      </div>

    </div>

    <!-- RESTRICCIONES DE GOOGLE FLIGHTS -->
    <div class="alert alert-info alerta-pasajeros py-2 px-3 mb-3" role="note">

      <div class="fw-bold mb-1">
        <i class="bi bi-info-circle"></i>
        Restricciones de pasajeros (Google Flights)
      </div>

      <ul class="mb-0 ps-3">
        <li>Máximo <strong>{{ MAX_PASAJEROS }} pasajeros</strong> en total, sumando todas las habitaciones.</li>
        <li>Debe viajar <strong>al menos 1 adulto</strong>.</li>
        <li>Máximo <strong>{{ MAX_INFANTES_POR_ADULTO }} infantes por adulto</strong>.</li>
        <li>Niños de <strong>12 a 17 años</strong> se cotizan con <strong>tarifa de adulto</strong> (también cuentan como adulto para el límite de infantes).</li>
        <li>Niños de <strong>2 a 11 años</strong>: tarifa de niño, sin límite por adulto.</li>
        <li>Infantes (<strong>0 a 1 año</strong>): se cotizan <strong>con asiento propio</strong>.</li>
      </ul>

    </div>

    <div
      v-if="restriccionActual"
      class="alert alert-warning py-2 px-3 mb-3"
      role="alert"
    >
      <i class="bi bi-exclamation-triangle"></i>
      No se puede cotizar así: {{ restriccionActual }}
    </div>

    <!-- LISTADO -->
    <div class="row">

      <div
        v-for="(hab, index) in habitaciones"
        :key="index"
        :class="colClass"
        class="mb-4"
      >

        <div class="card shadow-sm border-0 h-100">

          <!-- HEADER CARD -->
          <div class="card-header bg-light fw-bold">

            <i class="bi bi-house-door"></i>

            Habitación {{ index + 1 }}

          </div>

          <!-- BODY -->
          <div class="card-body">

            <!-- ADULTOS -->
            <div class="mb-3">

              <label class="form-label">
                Adultos
              </label>

              <input
                type="number"
                min="1"
                class="form-control"
                :value="hab.adultos"
                @input="setAdultos(index, $event.target.value, $event)"
              />

            </div>

            <!-- NIÑOS -->
            <div class="mb-3">

              <label class="form-label">
                Niños
              </label>

              <input
                type="number"
                min="0"
                class="form-control"
                :value="hab.ninos"
                @input="setNinos(index, $event.target.value, $event)"
              />

            </div>

            <!-- EDADES NIÑOS -->
            <div
              v-if="hab.edadesNinos?.length"
              class="row g-2 mb-3"
            >

              <div
                v-for="(edad, j) in hab.edadesNinos"
                :key="j"
                :class="colEdadClass(hab.edadesNinos.length)"
              >

                <label class="form-label small">

                  Niño {{ j + 1 }}

                </label>

                <input
                  type="number"
                  :min="EDAD_MIN_NINO"
                  :max="EDAD_MAX_NINO"
                  class="form-control"
                  :value="hab.edadesNinos[j]"
                  @change="setEdadNino(hab, j, $event)"
                />

              </div>

            </div>

            <!-- INFANTES -->
            <div class="mb-3">

              <label class="form-label">
                Infantes
              </label>

              <input
                type="number"
                min="0"
                class="form-control"
                :value="hab.infantes"
                @input="setInfantes(index, $event.target.value, $event)"
              />

            </div>

            <!-- EDADES INFANTES -->
            <div
              v-if="hab.edadesInfantes?.length"
              class="row g-2"
            >

              <div
                v-for="(edad, j) in hab.edadesInfantes"
                :key="j"
                :class="colEdadClass(hab.edadesInfantes.length)"
              >

                <label class="form-label small">

                  Infante {{ j + 1 }}

                </label>

                <input
                  type="number"
                  min="0"
                  :max="EDAD_MAX_INFANTE"
                  class="form-control"
                  :value="hab.edadesInfantes[j]"
                  @input="setEdadInfante(hab, j, $event)"
                  @blur="$event.target.value = hab.edadesInfantes[j]"
                />

              </div>

            </div>

          </div>

          <!-- FOOTER -->
          <div class="card-footer bg-white border-0 text-muted small">

            Total:
            {{
              Number(hab.adultos) +
              Number(hab.ninos) +
              Number(hab.infantes)
            }}
            pasajeros

          </div>

        </div>

      </div>

    </div>

  </div>

</template>

<style scoped>

.card {
  border-radius: 14px;
  overflow: hidden;
}

.card-header {
  font-size: 15px;
}

.form-control {
  border-radius: 10px;
}

.alerta-pasajeros {
  font-size: 13px;
  border-radius: 10px;
}

</style>